import React from "react"
import { makeStyles } from "tss-react/mui"
import { Divider, Grid, Stack, Typography } from "@mui/material"
import { TimelineCalendarEventTimeRange } from "../../../createOrder/components/resource/calendar/fullCalendarTypes"
import { LocalizedStrict } from "../../../../../modules/localization/components/AppLocalized"
import { MeetingDetailsTableSetting } from "./MeetingDetailsTableSetting"
import { TimeRangePicker } from "./TimeRangePicker"
import { ResourceItem, SelectedMainUserState } from "../../../createOrder/createOrderTypes"
import {
    BookingConfig,
    DynamicConfigKey,
    ExtendedDynamicConfigKey,
} from "../../../createOrder/components/form/formTypes"
import { DynamicFormField, DynamicFormFieldGroupWrap } from "../../../createOrder/components/form/DynamicFormField"
import { ResponsibleAutocomplete } from "../../../createOrder/components/form/ResponsibleAutocomplete"

const useStyles = makeStyles()((theme) => ({
    wrap: {
        color: theme.palette.tailwind.slate[950],
    },
    input: {
        "& .MuiInputBase-root": {
            borderRadius: ".75rem",
        },
    },
    switchWrap: {
        borderRadius: ".75rem",
        padding: ".75rem",
        background: theme.palette.tailwind.blue[50],

        "& label.MuiFormControlLabel-root": {
            margin: 0,
            justifyContent: "flex-start",
        },
        "& .MuiFormControl-root": {
            width: "auto",
        },
    },
    switchDescription: {
        color: theme.palette.text.secondary,
    },
}))

type MeetingDetailsProps = Readonly<{
    config: BookingConfig
    resourcesList: ResourceItem[]
    timeRange?: TimelineCalendarEventTimeRange
    user: SelectedMainUserState
    isLockedForOtherUser?: boolean
}>

const LOCKED_FOR_OTHER_USER_KEYS: readonly ExtendedDynamicConfigKey[] = [
    DynamicConfigKey.MEETING_NAME,
    DynamicConfigKey.PRIVATE_MEETING,
]

export function MeetingDetails({ resourcesList, timeRange, user, config, isLockedForOtherUser }: MeetingDetailsProps) {
    const { classes } = useStyles()

    const resourcesWithTableSetups = resourcesList.filter((resource) => resource.tableSetups)

    const groups = config.sections.flatMap(({ groups }) => groups)

    return (
        <Grid container flexDirection="column" gap="1.5rem" className={classes.wrap}>
            {timeRange && <TimeRangePicker timeRange={timeRange} />}

            {groups.map(({ fields, title }, groupIndex) => {
                const hasCustomComponent = fields.some(({ key }) => key === DynamicConfigKey.MEETING_RESPONSIBLE)

                return (
                    <React.Fragment key={groupIndex}>
                        {groupIndex > 0 && <Divider flexItem />}
                        {title && !hasCustomComponent && <Typography variant="subtitle1">{title}</Typography>}

                        {fields.map((config) => {
                            const { key, label, description } = config
                            const SKIPPED_KEYS: readonly ExtendedDynamicConfigKey[] = [
                                DynamicConfigKey.SERVICE_CENTER_MESSAGE,
                                DynamicConfigKey.TABLE_ARANGEMENTS,
                                DynamicConfigKey.ADD_PARTICIPANTS_FORM,
                            ]

                            if (SKIPPED_KEYS.includes(key)) {
                                return
                            }

                            if (key === DynamicConfigKey.MEETING_RESPONSIBLE) {
                                return (
                                    <DynamicFormFieldGroupWrap config={config} key={key}>
                                        <ResponsibleAutocomplete
                                            name={DynamicConfigKey.MEETING_RESPONSIBLE}
                                            label={label || description}
                                            user={user}
                                        />
                                    </DynamicFormFieldGroupWrap>
                                )
                            }

                            return (
                                <DynamicFormField
                                    config={config}
                                    user={user}
                                    key={key}
                                    disabled={Boolean(isLockedForOtherUser) && LOCKED_FOR_OTHER_USER_KEYS.includes(key)}
                                />
                            )
                        })}
                    </React.Fragment>
                )
            })}

            {groups.some((group) =>
                group.fields.some(({ key, active }) => key === DynamicConfigKey.TABLE_ARANGEMENTS && active),
            ) &&
                resourcesWithTableSetups.length > 0 && (
                    <>
                        <Divider />
                        <Stack gap="1rem">
                            <LocalizedStrict id="create-order-confirm-booking-table-setup">
                                <Typography variant="subtitle1">Table setup</Typography>
                            </LocalizedStrict>
                            <MeetingDetailsTableSetting resourcesList={resourcesWithTableSetups} />
                        </Stack>
                    </>
                )}
        </Grid>
    )
}
